import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { dailyLogsApi, type DailyLogRollup, type DailyLogRollupParams } from '@/lib/apiClient';
import { StateView, Badge } from '@/components';
import { fmtDate, isRealYmd, DATE_INPUT_MIN, DATE_INPUT_MAX } from '@/lib/dates';
import { statusBadge, weekday, weatherSummary, num, DAILY_LOGS_ROLLUP_KEY } from '../projects/dailyLogFormat';

/**
 * Company-wide Daily Logs (#617): every job's logs in one place, so nobody has to
 * open each job to find one. Office users only — the route sits behind flGuard
 * like the other rollups, and the API scopes the rows to the jobs the caller can
 * see.
 *
 * The filters live in the URL (?from=&to=&status=&q=) so a filtered view can be
 * bookmarked or sent to somebody. Leaving `from`/`to` off asks the server for its
 * default window (the last 30 days); the window it applied comes back with the
 * rows and is what the date boxes show.
 */

const SEARCH_DEBOUNCE_MS = 300;

const inRange = (ymd: string) => ymd >= DATE_INPUT_MIN && ymd <= DATE_INPUT_MAX;

/**
 * A well-formed date in the URL that is outside the supported range.
 *
 * ⚠️ A date box fires a change at every keystroke of the year, so typing 2026 passes
 * through 0002, 0020 and 0202 — each a real day, and each would ask the server for
 * centuries of history (which now answers 400). They are kept in the box as typed,
 * so the person can finish the year, but never sent.
 */
export function hasOutOfRangeDate(sp: URLSearchParams): boolean {
  return (['from', 'to'] as const).some((k) => {
    const v = sp.get(k);
    return !!v && isRealYmd(v) && !inRange(v);
  });
}

/** The filters the URL carries, validated: anything unrecognized is dropped, not sent. */
export function filtersFromSearch(sp: URLSearchParams): DailyLogRollupParams {
  const from = sp.get('from');
  const to = sp.get('to');
  const status = sp.get('status');
  const q = sp.get('q')?.trim();
  return {
    ...(from && isRealYmd(from) && inRange(from) ? { from } : {}),
    ...(to && isRealYmd(to) && inRange(to) ? { to } : {}),
    ...(status === 'draft' || status === 'submitted' ? { status } : {}),
    ...(q ? { q: q.slice(0, 100) } : {}),
  };
}

/**
 * Whether the person has text selected INSIDE this row.
 *
 * A plain click collapses any selection on mouse-down, so a selection that is
 * still there when the click lands means the click ended a drag-select — somebody
 * copying a job name or a summary, not asking to open the row. Without this the
 * drag navigated away and lost both the selection and the filtered view.
 *
 * Scoped to the row on purpose. A selection made elsewhere (the search box, another
 * row) says nothing about this click, and treating it as one would leave the row
 * dead until the person clicked somewhere neutral. A drag starts or ends in the row
 * it was made in, so its anchor or focus is inside it.
 */
function hasTextSelectionIn(row: HTMLElement): boolean {
  const sel = window.getSelection();
  if (!sel || sel.isCollapsed || sel.toString().trim() === '') return false;
  return (!!sel.anchorNode && row.contains(sel.anchorNode)) || (!!sel.focusNode && row.contains(sel.focusNode));
}

const css = `
  /* Wide on purpose: eight columns, and the page is for scanning many jobs. The
     table still scrolls inside its card below that, and becomes cards on a phone. */
  .dlr-scroll { max-width: 1600px; margin: 0 auto; }
  .dlr-eyebrow {
    font: var(--ew-fw-medium) var(--ew-fs-meta)/1 var(--ew-font);
    letter-spacing: var(--ew-ls-caption); text-transform: uppercase; color: var(--ew-n-400);
  }
  .dlr-title {
    font: var(--ew-fw-semibold) 28px/1.15 var(--ew-font); letter-spacing: -0.01em;
    color: var(--ew-dark); margin: 6px 0 var(--ew-s-5);
  }
  .dlr-card { background: #fff; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); margin-bottom: var(--ew-s-4); }
  .dlr-card-hd {
    display: flex; align-items: baseline; justify-content: space-between; gap: var(--ew-s-3); flex-wrap: wrap;
    font: var(--ew-fw-semibold) var(--ew-fs-body-sm)/1 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase; color: var(--ew-n-500); margin: 0; padding: 14px var(--ew-s-4);
    border-bottom: 1px solid var(--ew-n-100);
  }
  .dlr-count { font-weight: var(--ew-fw-regular); text-transform: none; letter-spacing: 0; }

  .dlr-filters { display: flex; flex-wrap: wrap; gap: var(--ew-s-3); align-items: flex-end; padding: var(--ew-s-4); }
  .dlr-field { display: flex; flex-direction: column; min-width: 0; }
  .dlr-field--grow { flex: 1 1 240px; }
  .dlr-label {
    font: var(--ew-fw-medium) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase; color: var(--ew-n-500); margin-bottom: 5px;
  }
  .dlr-input {
    border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); padding: 7px 10px; min-height: 36px;
    font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); background: #fff; color: var(--ew-dark);
    width: 100%; box-sizing: border-box;
  }
  .dlr-reset {
    background: transparent; border: 0; cursor: pointer; padding: 8px 4px; min-height: 36px;
    color: var(--ew-n-500); font: var(--ew-fw-medium) var(--ew-fs-body-sm) var(--ew-font);
    text-decoration: underline;
  }
  .dlr-reset:hover { color: var(--ew-dark); }

  .dlr-table { width: 100%; border-collapse: collapse; font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); }
  .dlr-table th {
    text-align: left; padding: 10px var(--ew-s-3); white-space: nowrap;
    font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase; color: var(--ew-n-500); border-bottom: 1px solid var(--ew-n-100);
  }
  .dlr-table td { padding: 11px var(--ew-s-3); border-bottom: 1px solid var(--ew-n-100); color: var(--ew-dark); vertical-align: top; }
  .dlr-table tr:last-child td { border-bottom: 0; }
  .dlr-row { cursor: pointer; transition: background var(--ew-dur-fast) var(--ew-ease); }
  .dlr-row:hover { background: var(--ew-n-50); }
  .dlr-muted { color: var(--ew-n-500); }
  .dlr-num { font-variant-numeric: tabular-nums; white-space: nowrap; }
  .dlr-job { font-weight: var(--ew-fw-medium); color: var(--ew-dark); text-decoration: none; white-space: nowrap; }
  .dlr-job:hover { text-decoration: underline; }
  .dlr-job-title { color: var(--ew-n-500); font-size: var(--ew-fs-meta); margin-top: 2px; max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .dlr-summary { max-width: 300px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .dlr-stale { opacity: 0.55; transition: opacity var(--ew-dur-fast) var(--ew-ease); }
  .dlr-notice {
    margin: 0; padding: 10px var(--ew-s-4); background: var(--ew-n-50); color: var(--ew-n-600);
    border-top: 1px solid var(--ew-n-100); font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font);
  }
`;

export default function DailyLogsRollup() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const filters = filtersFromSearch(params);
  const filterKey = JSON.stringify(filters);
  const badDate = hasOutOfRangeDate(params);
  // What the date box shows: what is in the URL as typed (even half a year), else
  // the window the server applied.
  const shownDate = (key: 'from' | 'to', applied: string | undefined) => {
    const v = params.get(key);
    return v && isRealYmd(v) ? v : applied ?? '';
  };

  const query = useQuery<DailyLogRollup>({
    queryKey: [...DAILY_LOGS_ROLLUP_KEY, filterKey],
    queryFn: () => dailyLogsApi.rollup(filters),
    staleTime: 60_000,
    // Keep the previous rows on screen (dimmed) while a changed filter loads,
    // rather than blanking the table on every keystroke.
    placeholderData: (prev) => prev,
  });

  // The search box is typed into, so it needs its own state; the URL is updated
  // once typing pauses.
  const [qText, setQText] = useState(filters.q ?? '');
  // The URL can change under the box — the Daily Logs tab clicked while filtered,
  // or Back. Pull it in, but never while it already agrees with what is typed, or
  // the trailing space of "tile " would be stripped mid-keystroke. Without this the
  // stale text below would re-apply a search the person just cleared.
  useEffect(() => {
    setQText((cur) => (cur.trim() === (filters.q ?? '') ? cur : (filters.q ?? '')));
  }, [filters.q]);
  useEffect(() => {
    const wanted = qText.trim();
    if (wanted === (filters.q ?? '')) return;
    const t = setTimeout(() => setParam('q', wanted), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qText, filters.q]);

  // Reads the LATEST params through a ref, on purpose: the search box fires this
  // from a timer, and the `params` captured when the timer was set would drop any
  // date or status the person changed while it was pending. (React Router's
  // functional `setParams(prev => …)` does not fix it — `prev` is the snapshot
  // from the render that created the callback, not a queued state.)
  const latest = useRef(params);
  latest.current = params;
  function setParam(key: 'from' | 'to' | 'status' | 'q', value: string) {
    const next = new URLSearchParams(latest.current);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  }
  function reset() {
    setQText('');
    setParams(new URLSearchParams(), { replace: true });
  }

  const data = query.data;
  const filtered = Object.keys(filters).length > 0 || badDate;
  const openLog = (projectId: number, id: number) =>
    navigate(`/projects/${projectId}?tab=daily&log=${id}`);

  return (
    <>
      <style>{css}</style>
      <div className="dlr-scroll">
        <div className="dlr-eyebrow">Company-wide</div>
        <h1 className="dlr-title">Daily Logs</h1>

        <div className="dlr-card">
          <div className="dlr-filters" role="search" aria-label="Filter daily logs">
            <div className="dlr-field">
              <label className="dlr-label" htmlFor="dlr-from">From</label>
              <input
                id="dlr-from" type="date" className="dlr-input"
                min={DATE_INPUT_MIN} max={DATE_INPUT_MAX}
                value={shownDate('from', data?.from)}
                onChange={(e) => setParam('from', e.target.value)}
              />
            </div>
            <div className="dlr-field">
              <label className="dlr-label" htmlFor="dlr-to">To</label>
              <input
                id="dlr-to" type="date" className="dlr-input"
                min={DATE_INPUT_MIN} max={DATE_INPUT_MAX}
                value={shownDate('to', data?.to)}
                onChange={(e) => setParam('to', e.target.value)}
              />
            </div>
            <div className="dlr-field">
              <label className="dlr-label" htmlFor="dlr-status">Status</label>
              <select
                id="dlr-status" className="dlr-input"
                value={filters.status ?? ''}
                onChange={(e) => setParam('status', e.target.value)}
              >
                <option value="">All</option>
                <option value="submitted">Submitted</option>
                <option value="draft">Draft</option>
              </select>
            </div>
            <div className="dlr-field dlr-field--grow">
              <label className="dlr-label" htmlFor="dlr-q">Search</label>
              <input
                id="dlr-q" type="search" className="dlr-input" maxLength={100}
                placeholder="Job number or name, author, or work done"
                value={qText}
                onChange={(e) => setQText(e.target.value)}
              />
            </div>
            {filtered && (
              <button type="button" className="dlr-reset" onClick={reset}>Reset filters</button>
            )}
          </div>
          {badDate && (
            <p className="dlr-notice" role="status">
              Choose dates between {fmtDate(DATE_INPUT_MIN)} and {fmtDate(DATE_INPUT_MAX)}. A date outside
              that range is not used.
            </p>
          )}
        </div>

        <div className="dlr-card">
          <h2 className="dlr-card-hd">
            Logs
            {data && (
              <span className="dlr-count">
                {data.rows.length.toLocaleString('en-US')} {data.rows.length === 1 ? 'log' : 'logs'}
                {' · '}{fmtDate(data.from)} – {fmtDate(data.to)}
              </span>
            )}
          </h2>
          <StateView
            query={query}
            isEmpty={(d) => d.rows.length === 0}
            empty={filtered ? 'No daily logs match these filters.' : 'No daily logs in this period.'}
          >
            {(d) => (
              <div className={query.isPlaceholderData ? 'dlr-stale' : undefined}>
                <div className="ew-table-scroll">
                  <table className="dlr-table ew-cards">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Job</th>
                        <th>Author</th>
                        <th>Weather</th>
                        <th>Workers</th>
                        <th>Man-hours</th>
                        <th>Summary</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.rows.map((r) => {
                        const b = statusBadge(r.status);
                        return (
                          <tr key={r.id} className="dlr-row" onClick={(e) => { if (!hasTextSelectionIn(e.currentTarget)) openLog(r.projectId, r.id); }}>
                            <td className="dlr-num" data-label="Date">
                              {fmtDate(r.date)}
                              <span className="dlr-muted"> · {weekday(r.date)}</span>
                            </td>
                            <td data-label="Job">
                              {/* A real link, so the keyboard and middle-click work; the row click is a convenience on top. */}
                              <Link
                                className="dlr-job"
                                to={`/projects/${r.projectId}?tab=daily&log=${r.id}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {r.jobNumber}
                              </Link>
                              {r.projectTitle && <div className="dlr-job-title" title={r.projectTitle}>{r.projectTitle}</div>}
                            </td>
                            <td className="dlr-muted" data-label="Author">{r.author ?? '—'}</td>
                            <td className="dlr-muted" data-label="Weather">{weatherSummary(r)}</td>
                            <td className="dlr-num" data-label="Workers">{num(r.totalWorkers)}</td>
                            <td className="dlr-num" data-label="Man-hours">{num(r.totalManhours)}</td>
                            <td className="dlr-muted dlr-summary" data-label="Summary" title={r.workSummary?.trim() || undefined}>
                              {r.workSummary?.trim() || '—'}
                            </td>
                            <td data-label="Status"><Badge label={b.label} tone={b.tone} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {d.truncated && (
                  <p className="dlr-notice" role="status">
                    Showing the newest {d.cap.toLocaleString('en-US')} logs. Narrow the dates or search to see the rest.
                  </p>
                )}
              </div>
            )}
          </StateView>
        </div>
      </div>
    </>
  );
}
